<script module lang="ts">
  // Pages may mount the layout anew, so the path of the previous visit lives outside of the component.
  let previousPath: string | undefined
</script>

<script lang="ts">
  import type { Snippet } from 'svelte'
  import { page } from '@inertiajs/svelte'
  import { translate } from '../i18n'
  import I18nProvider from './I18nProvider.svelte'
  import Navigation from './Navigation.svelte'
  import Footer from './Footer.svelte'

  let { children, title }: { children: Snippet; title?: string } = $props()

  const locale = $derived(page.props.locale)
  const documentTitle = $derived(title ? `${title} | Visit Augsburg` : 'Visit Augsburg')
  const path = $derived(page.url.split(/[?#]/)[0])

  let main: HTMLElement
  let announcement = $state('')

  // Inertia swaps the page without a browser navigation, so focus and screen readers would stay on the old page.
  // Visits that keep the path (search, pagination) leave the focus where it is.
  $effect(() => {
    if (previousPath !== undefined && previousPath !== path) {
      main.focus({ preventScroll: true })
      announcement = documentTitle
    }
    previousPath = path
  })

  $effect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = page.props.direction
  })
</script>

<svelte:head>
  <title>{documentTitle}</title>
</svelte:head>

<I18nProvider {locale}>
  <div class="layout">
    <a class="layout-skip-link" href="#main">{translate(locale, 'navigation.skipToContent')}</a>
    <p class="layout-announcement" aria-live="polite">{announcement}</p>
    <div class="layout-navigation">
      <Navigation />
    </div>
    <main id="main" class="layout-body" tabindex="-1" bind:this={main}>
      {@render children?.()}
    </main>
    <div class="layout-footer">
      <Footer />
    </div>
  </div>
</I18nProvider>

<style>
  .layout {
    min-height: 100vh;
  }

  .layout-skip-link {
    position: absolute;
    inset-block-start: var(--space-4);
    inset-inline-start: var(--space-4);
    z-index: 1;
    padding: var(--space-4) var(--space-7);
    border-radius: var(--radius-6);
    background-color: var(--color-variant-brand);
    color: var(--color-white);
    transform: translateY(calc(-100% - var(--space-8)));

    &:focus {
      transform: none;
    }
  }

  .layout-announcement {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .layout-navigation {
    display: block;
    height: var(--nav-height);
    max-width: var(--breakpoint-content);
    margin: var(--space-6) auto 0;
    padding: 0 var(--space-4);

    @media (--tablet) {
      padding: 0 var(--space-9);
    }
  }

  .layout-body {
    padding: var(--page-gutter);
    max-width: var(--breakpoint-content);
    margin-inline: auto;

    &:focus {
      outline: none;
    }
  }

  .layout-footer {
    padding-inline: var(--page-gutter);
    max-width: var(--breakpoint-content);
    margin-inline: auto;
  }
</style>
